import httpClient from './httpClient';

export type SearchCategory =
  | 'courses'
  | 'users'
  | 'content'
  | 'assessments'
  | 'projects'
  | 'certifications'
  | 'feedback'
  | 'ilt'
  | 'reports'
  | 'pages';

export interface SearchResult {
  id: string;
  category: SearchCategory;
  title: string;
  description?: string;
  url: string;
  metadata?: Record<string, string>;
  score?: number;
}

export interface SearchResponse {
  success: boolean;
  data?: {
    results: SearchResult[];
    total: number;
    query: string;
  };
  error?: {
    message: string;
  };
}

export interface TrendingItem {
  id: string;
  category: SearchCategory;
  title: string;
  url: string;
}

class GlobalSearchService {
  /**
   * Perform a global search across all entities
   */
  async search(query: string, categories?: SearchCategory[]): Promise<SearchResponse> {
    try {
      const params = new URLSearchParams({ q: query });
      if (categories && categories.length > 0) {
        params.append('categories', categories.join(','));
      }

      const response = await httpClient.get<SearchResponse>(`/search?${params.toString()}`);
      return {
        success: true,
        data: response as any,
      };
    } catch (error: any) {
      console.error('Search error:', error);
      return {
        success: false,
        error: {
          message: error.response?.data?.message || 'Search failed. Please try again.',
        },
      };
    }
  }

  /**
   * Get trending/popular items
   */
  async getTrending(): Promise<TrendingItem[]> {
    try {
      const response = await httpClient.get<TrendingItem[]>('/search/trending');
      return response || [];
    } catch (error) {
      console.error('Failed to fetch trending items:', error);
      return [];
    }
  }

  /**
   * Search within a specific category only
   */
  async searchByCategory(query: string, category: SearchCategory): Promise<SearchResponse> {
    return this.search(query, [category]);
  }

  /**
   * Get quick access pages based on user role
   */
  getQuickAccessPages(userRole: string): SearchResult[] {
    const commonPages: SearchResult[] = [
      {
        id: 'page-home',
        category: 'pages',
        title: 'Dashboard',
        description: 'View your dashboard and overview',
        url: `/${userRole}/dashboard`,
      },
      {
        id: 'page-settings',
        category: 'pages',
        title: 'Account Settings',
        description: 'Manage your account settings',
        url: '/account-settings',
      },
      {
        id: 'page-support',
        category: 'pages',
        title: 'Support & Help',
        description: 'Get help and support',
        url: '/support',
      },
    ];

    // Role-specific pages
    if (userRole === 'trainee') {
      return [
        ...commonPages,
        {
          id: 'page-browse',
          category: 'pages',
          title: 'Browse Courses',
          description: 'Explore available courses',
          url: '/trainee/my-learnings/browse-courses',
        },
        {
          id: 'page-mycourses',
          category: 'pages',
          title: 'My Courses',
          description: 'View your enrolled courses',
          url: '/trainee/my-learnings/my-course',
        },
        {
          id: 'page-achievements',
          category: 'pages',
          title: 'Achievements',
          description: 'View your achievements and badges',
          url: '/trainee/achievements',
        },
        {
          id: 'page-transcript',
          category: 'pages',
          title: 'Transcript',
          description: 'View your learning transcript',
          url: '/trainee/transcript',
        },
      ];
    }

    if (userRole === 'admin' || userRole === 'superadmin' || userRole === 'trainer') {
      const prefix = userRole === 'trainer' ? 'trainer' : userRole === 'superadmin' ? 'superadmin' : 'admin';
      return [
        ...commonPages,
        {
          id: 'page-courses',
          category: 'pages',
          title: 'Course Management',
          description: 'Manage courses and curriculum',
          url: `/${prefix}/content/courses`,
        },
        {
          id: 'page-users',
          category: 'pages',
          title: 'User Management',
          description: 'Manage users and teams',
          url: `/${prefix}/users/list`,
        },
        {
          id: 'page-content',
          category: 'pages',
          title: 'Content Management',
          description: 'Manage learning content',
          url: `/${prefix}/content/management`,
        },
        {
          id: 'page-reports',
          category: 'pages',
          title: 'Reports & Analytics',
          description: 'View reports and analytics',
          url: `/${prefix}/reports`,
        },
      ];
    }

    return commonPages;
  }
}

export default new GlobalSearchService();
